import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Badge, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing } from "lucide-react";

const fmt = (iso) => (iso ? iso.slice(0, 16).replace("T", " ") : "—");
const hoursSince = (iso) => (iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 36e5)) : 0);

export default function LuggageHistory({ branches, isManager, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ branch: "", status: "" });
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const p = new URLSearchParams();
        if (filters.branch) p.append("branch", filters.branch);
        if (filters.status === "overdue") p.append("overdue", "true");
        else if (filters.status) p.append("status", filters.status);
        const { data } = await api.get(`/luggage/sessions?${p.toString()}`);
        setItems(data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [filters, refreshKey]);

  const runOverdueCheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/luggage/run-overdue-check");
      toast.success(data.flagged
        ? `${data.flagged} overdue bag(s) flagged — branch manager(s) alerted by email`
        : `No new overdue bags (threshold ${data.hours}h)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setChecking(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {isManager && (
          <select value={filters.branch} data-testid="lug-hist-branch"
            onChange={(e) => setFilters((f) => ({ ...f, branch: e.target.value }))}
            className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm">
            <option value="">All branches</option>
            {branches.map((b) => <option key={b.id} value={b.name}>{b.name}</option>)}
          </select>
        )}
        <select value={filters.status} data-testid="lug-hist-status"
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-sm">
          <option value="">All sessions</option>
          <option value="open">Still stored (open)</option>
          <option value="overdue">Overdue (needs follow-up)</option>
          <option value="closed">Collected (closed)</option>
        </select>
        {isManager && (
          <Button variant="outline" size="sm" onClick={runOverdueCheck} disabled={checking}
            className="ml-auto" data-testid="lug-run-overdue-check">
            <BellRing size={14} className="mr-1.5" />
            {checking ? "Checking…" : "Run overdue check"}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-gray-500 py-12 text-center">Loading history…</div>
      ) : items.length === 0 ? (
        <EmptyState title="No luggage sessions yet"
          hint="Every deposit and collection will appear here with who served it and when." />
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto" data-testid="lug-history-table">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
                <th className="px-3 py-2.5">Locker</th>
                <th className="px-3 py-2.5">Branch</th>
                <th className="px-3 py-2.5">Customer</th>
                <th className="px-3 py-2.5">Items</th>
                <th className="px-3 py-2.5">Stored</th>
                <th className="px-3 py-2.5">Collected</th>
                <th className="px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60">
                  <td className="px-3 py-2.5 font-display font-bold text-emerald-900">{s.locker_no}</td>
                  <td className="px-3 py-2.5 text-gray-600">{s.branch}</td>
                  <td className="px-3 py-2.5 text-gray-700">
                    {s.customer_name || "—"}
                    {s.customer_phone && <div className="text-[11px] text-gray-400">{s.customer_phone}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500 max-w-[220px] truncate">{s.items_description || "—"}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600">
                    {fmt(s.checked_in_at)}
                    {s.checked_in_by_name && <div className="text-[11px] text-gray-400">by {s.checked_in_by_name}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-600">
                    {fmt(s.checked_out_at)}
                    {s.checked_out_by_name && <div className="text-[11px] text-gray-400">by {s.checked_out_by_name}</div>}
                  </td>
                  <td className="px-3 py-2.5">
                    {s.is_overdue ? (
                      <Badge tone="red">overdue {hoursSince(s.checked_in_at)}h</Badge>
                    ) : (
                      <Badge tone={s.status === "open" ? "amber" : "emerald"}>
                        {s.status === "open" ? "stored" : "collected"}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
